import { ReactElement, useMemo, useRef, useState } from 'react';
import {
    UIButton,
    UIMenu,
    UIMenuItem,
    UIPopover,
    UIPropoverRef,
    UIText
} from '@/ui';
import { blue } from '@/ui/styles';
import { useFormatDate } from '@/common/hooks';
import styles from './task-last-location-panel.module.scss';
import { useTaskBeaconSnapshot, useTaskLocations } from '../providers';
import { useTasksTranslation } from '../hooks';
import { TaskBeaconsList } from './task-beacons-list';
import { TaskLastLocationList } from './task-last-location-list';

/**
 * The task's last known location and its nearby beacons, behind a picker that
 * swaps between them. The two panel names and the "Updated …" line are task
 * vocabulary, shared with `MiniTaskDetail`.
 */
export function TaskLastLocationPanel(): ReactElement {
    const ref = useRef<UIPropoverRef>(null);
    const { t } = useTasksTranslation();
    const { formatSmartShortTimeAndDate } = useFormatDate();

    const [selectedIndex, setSelectedIndex] = useState(0);
    // The same two keys `MiniTaskDetail` reads, so the panel is named the same
    // in both places.
    const buttons = useMemo(
        () => [
            t('shared.tasks.section_last_known_location'),
            t('shared.tasks.section_nearby_beacons')
        ],
        [t]
    );
    const buttonText = buttons[selectedIndex];

    function onClick(index: number) {
        setSelectedIndex(index);
        ref.current?.close();
    }

    const { latestLocation } = useTaskLocations();
    const { updatedAtStr } = useTaskBeaconSnapshot();

    const timeString = useMemo(() => {
        if (selectedIndex === 0) {
            if (latestLocation) {
                return t('shared.tasks.updated_at', {
                    time: formatSmartShortTimeAndDate(
                        new Date(latestLocation.ts)
                    )
                });
            }
        } else if (selectedIndex === 1) {
            if (updatedAtStr) {
                return t('shared.tasks.updated_at', {
                    time: updatedAtStr
                });
            }
        }
        return '';
    }, [
        formatSmartShortTimeAndDate,
        latestLocation,
        updatedAtStr,
        selectedIndex,
        t
    ]);

    return (
        <div className={styles.container}>
            <div className={styles.sectionHeaderWrapper}>
                <UIPopover
                    ref={ref}
                    placement="bottom_right"
                    popoverPadding={4}
                    trigger="click"
                    triggerNode={
                        <button className={styles.removeButtonStyle}>
                            <UIButton
                                variant="textPrimary"
                                rightIcon="chevron-down"
                                withoutButtonHtmlTag
                                customPadding={8}
                            >
                                {buttonText}
                            </UIButton>
                        </button>
                    }
                >
                    <UIMenu>
                        {buttons.map((button, index) => (
                            <UIMenuItem
                                key={index}
                                text={button}
                                onClick={() => {
                                    onClick(index);
                                }}
                                leftIconName={
                                    selectedIndex === index
                                        ? 'check'
                                        : undefined
                                }
                                leftIconColor={blue}
                            />
                        ))}
                    </UIMenu>
                </UIPopover>

                <UIText type="default2" variant="small">
                    {timeString}
                </UIText>
            </div>
            {selectedIndex === 0 && (
                <TaskLastLocationList
                    onViewAllBeacons={() => {
                        setSelectedIndex(1);
                    }}
                />
            )}
            {selectedIndex === 1 && <TaskBeaconsList />}
        </div>
    );
}
